import { useState, type CSSProperties } from 'react';
import { App, Button, Checkbox, DatePicker, Dropdown, Flex, Input, Segmented, Select, Space, Typography } from 'antd';
import type { ButtonProps, MenuProps } from 'antd';
import { EllipsisOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { dec } from '@app/shared';
import { FloatingWindow } from '@/shared/ui/FloatingWindow';
import { HotkeyButton, HotkeyTip } from '@/shared/ui/hotkey';
import { useUnsavedClose } from '@/shared/ui';
import { INVALID_INPUTS_HINT, INVALID_INPUTS_REASON, InvalidInputsContext, MoneyInput, commitActiveField, useInvalidInputs } from '@/shared/ui/inputs';
import { useHotkeyActions } from '@/shared/lib/hotkeys';
import { useBreakpoint, useLatest } from '@/shared/lib/hooks';
import { CounterpartySelect } from '@/entities/counterparty';
import {
  useCancelDocumentMutation,
  useCashOrderTemplateQuery,
  useCreateDocumentMutation,
  useDeleteDocumentMutation,
  useGetDocumentQuery,
  useListDocumentsQuery,
  usePostDocumentMutation,
  usePreviewDocumentMutation,
  useRestoreDocumentMutation,
  useReturnDocumentToDraftMutation,
  useUnpostDocumentMutation,
  useUpdateDocumentMutation,
} from '@/entities/document';
import type { DocumentCounterpartyRef, DocumentDto, LineWarningEntry, SaveDocumentPayload } from '@/entities/document';
import type { PaymentMethod } from '@/entities/payment';
import { useCashOrderPaymentQuery } from '@/entities/payment';
import { AllocationGrid, AllocationTotals, allocationRows, allocationSeedKey, allocationTarget, sumAmounts, toManualAllocations } from '@/features/payment/allocation-grid';
import type { AllocationRow } from '@/features/payment/allocation-grid';
import { PreviewConsequencesDialog, useSaveWithPreview } from '@/features/document/preview-consequences';
import { isVersionConflict } from '@/features/document/concurrent-edit';
import { AttachmentsBlock } from '@/features/attachments';
import { useStoreContext } from '@/shared/lib/store-context';
import { todayInTz } from '@/shared/lib/date';
import { describeWarnings } from '@/shared/lib/warnings';
import { translateApiError } from '@/shared/api/errorToMessage';
import type { CashOrderHeader, CashOrderType } from './model';
import { clampAllocations, directionOf, getCashOrderBlockReason, refitPrefill, windowTitle } from './model';
import './CashOrderWindow.mobile.css';

const METHOD_OPTIONS: { value: PaymentMethod; label: string }[] = [
  { value: 'CASH', label: 'Наличные' },
  { value: 'CASHLESS', label: 'Безналичные' },
  { value: 'TRANSFER', label: 'Перевод' },
];

/** Телефон: поля формы — один столбец на всю ширину; пункты меню «⋯» — высотой под палец. */
const MOBILE_FIELD: CSSProperties = { width: '100%', minWidth: 0 };
const MOBILE_MENU_ITEM: CSSProperties = { minHeight: 44, display: 'flex', alignItems: 'center' };

export interface CashOrderWindowProps {
  type: CashOrderType;
  id?: string;
  /** §7 «Создать приходный/расходный ордер»: id продажи/прихода/возврата-основания. */
  baseDocumentId?: string;
  onClose: () => void;
}

/** §7: приходный/расходный кассовый ордер — полноценный документ (черновик → проведён →
 * отменён/удалён, как остальные), но без строк: сумма/способ/операция вводятся в шапке, а
 * разнесение по документам-должникам — той же сеткой, что и в `PaymentWindow`. */
export function CashOrderWindow({ type, id, baseDocumentId, onClose }: CashOrderWindowProps) {
  const { message, modal } = App.useApp();
  const { isMobile } = useBreakpoint();
  const labelSize = isMobile ? 13 : 12;
  const ctx = useStoreContext();
  const [documentId, setDocumentId] = useState(id);
  const direction = directionOf(type);

  const { data: doc, isFetching, isLoading: isLoadingDoc, refetch: refetchDoc } = useGetDocumentQuery(documentId ?? '', { skip: !documentId });
  const [createDocument, { isLoading: isCreating }] = useCreateDocumentMutation();
  const [updateDocument, { isLoading: isUpdating }] = useUpdateDocumentMutation();
  const [postDocument, { isLoading: isPosting }] = usePostDocumentMutation();
  const [unpostDocument, { isLoading: isUnposting }] = useUnpostDocumentMutation();
  const [cancelDocument, { isLoading: isCancelling }] = useCancelDocumentMutation();
  const [deleteDocument, { isLoading: isDeleting }] = useDeleteDocumentMutation();
  const [returnToDraft, { isLoading: isReturningToDraft }] = useReturnDocumentToDraftMutation();
  const [restoreDocument, { isLoading: isRestoring }] = useRestoreDocumentMutation();
  const [previewDocument] = usePreviewDocumentMutation();
  const [isPosting_, setIsPosting_] = useState(false);
  const { runWithPreview, pendingDiff, resolvePending, isRunningPreview } = useSaveWithPreview();

  const [header, setHeader] = useState<CashOrderHeader>({
    docDate: todayInTz(ctx.timezone),
    amount: null,
    method: 'CASH',
    cashOperationType: 'PAYMENT',
    comment: '',
  });
  const [counterpartyRef, setCounterpartyRef] = useState<DocumentCounterpartyRef | undefined>(undefined);
  const [noAutoAllocate, setNoAutoAllocate] = useState(false);
  const [rows, setRows] = useState<AllocationRow[]>([]);
  const [rowsDirty, setRowsDirty] = useState(false);
  // Несохранённые изменения: без этого `Esc` закрыл бы окно вместе с введённым.
  const [dirty, setDirty] = useState(false);

  /** Правка шапки пользователем (в отличие от заполнения из загруженного документа или шаблона). */
  function editHeader(patch: Partial<CashOrderHeader>) {
    setDirty(true);
    setHeader((h) => ({ ...h, ...patch }));
    // Строка-основание повторяет сумму ордера (но не выше долга), пока разнесение не правили вручную.
    if ('amount' in patch && baseDocumentId && !rowsDirty) setRows((current) => refitPrefill(current, baseDocumentId, patch.amount ?? null));
  }

  function seedHeaderFrom(d: DocumentDto) {
    setDirty(false);
    setHeader({
      docDate: d.docDate.slice(0, 10),
      counterpartyId: d.counterpartyId ?? undefined,
      amount: d.totalAmount,
      method: (d.method as PaymentMethod | null) ?? 'CASH',
      cashOperationType: d.cashOperationType ?? 'PAYMENT',
      comment: d.comment ?? '',
    });
    setCounterpartyRef(d.counterparty ?? undefined);
  }

  // Загруженный документ → заполнить шапку один раз (render-phase, не эффект — тот же приём, что и
  // в DocumentWindow/PaymentWindow).
  // Несохранённые правки владельца при обновлении документа с сервера не затираем.
  const [seededDoc, setSeededDoc] = useState<DocumentDto | undefined>(undefined);
  if (doc && doc !== seededDoc && (!dirty || !seededDoc)) {
    setSeededDoc(doc);
    seedHeaderFrom(doc);
  }

  // §7 «Создать приходный/расходный ордер»: новое окно, открытое с основанием, — контрагент, вид
  // операции и сумма остатка долга подтягиваются один раз, тем же паттерном.
  const { data: template, isLoading: isLoadingTemplate } = useCashOrderTemplateQuery(baseDocumentId ?? '', { skip: !baseDocumentId || !!id });
  const [seededTemplateFrom, setSeededTemplateFrom] = useState<string | undefined>(undefined);
  if (template && baseDocumentId && baseDocumentId !== seededTemplateFrom) {
    setSeededTemplateFrom(baseDocumentId);
    setHeader((h) => ({ ...h, counterpartyId: template.counterpartyId ?? undefined, cashOperationType: template.cashOperationType, amount: template.suggestedAmount }));
    setCounterpartyRef(template.counterparty ?? undefined);
  }

  const status = doc?.status ?? 'DRAFT';
  const isNew = !documentId;
  // Окно открыто на существующем ордере (или на основании) и данных ещё нет — вместо пустой формы
  // лоадер. Смотрим на проп `id`, а не на `documentId`: после первого сохранения нового ордера его id
  // появляется, но форма уже заполнена и прятать её нельзя.
  const isLoadingWindow = (!!id && isLoadingDoc) || isLoadingTemplate;
  const isDeleted = !!doc?.deletedAt;
  const isReadOnly = status === 'CANCELLED' || isDeleted;
  const busy = isCreating || isUpdating || isPosting || isPosting_ || isUnposting || isCancelling || isDeleting || isReturningToDraft || isRestoring || isRunningPreview;

  // Разнесение и «Не разносить» не хранятся на документе, только на связанном Payment (§7).
  const { data: paymentData, isLoading: isLoadingPayment } = useCashOrderPaymentQuery(documentId ?? '', { skip: !documentId });
  const existingPayment = paymentData?.payment;
  const target = allocationTarget(header.cashOperationType, direction);
  const { data: sideList, isFetching: isFetchingSideList } = useListDocumentsQuery(
    { type: target.documentType, counterpartyIds: header.counterpartyId ? [header.counterpartyId] : [], statuses: ['POSTED'], paymentStatuses: ['UNPAID', 'PARTIAL'] },
    { skip: !header.counterpartyId },
  );
  const [seededSideKey, setSeededSideKey] = useState<string | undefined>(undefined);
  const sideKey = sideList
    ? allocationSeedKey(`${header.cashOperationType}:${direction}`, existingPayment?.rowVersion, sideList.items, existingPayment?.allocations ?? [])
    : undefined;
  if (sideKey && sideKey !== seededSideKey && !rowsDirty) {
    setSeededSideKey(sideKey);
    setRows(allocationRows(sideList!.items, existingPayment?.allocations ?? [], target, baseDocumentId, header.amount));
    setNoAutoAllocate(existingPayment?.doNotAllocate ?? false);
  }

  function editRows(next: AllocationRow[]) {
    setRowsDirty(true);
    setDirty(true);
    setRows(next);
  }

  /** Кнопки диалогов подтверждения: на телефоне — 44px; на десктопе свойства те же, что были. */
  function confirmButton(base?: ButtonProps): ButtonProps | undefined {
    return isMobile ? { ...base, size: 'large' } : base;
  }

  function handleMutationError(e: unknown) {
    if (!isVersionConflict(e)) {
      message.error(translateApiError(e));
      return;
    }
    modal.confirm({
      title: 'Документ изменён в другом окне',
      content: 'Кто-то успел сохранить более новую версию. Открыть свежую версию? Ваши несохранённые изменения будут потеряны.',
      okText: 'Открыть свежую версию',
      cancelText: 'Отмена',
      okButtonProps: confirmButton(),
      cancelButtonProps: confirmButton(),
      autoFocusButton: 'cancel',
      onOk: async () => {
        const result = await refetchDoc();
        if (result.data) seedHeaderFrom(result.data);
      },
    });
  }

  const invalidInputs = useInvalidInputs();
  // Причина недоступности («Укажите сумму») нужна и подсказке на отключённой кнопке, и сообщению,
  // если действие вызвали горячей клавишей; `null` — действие доступно.
  const saveBlockReason = invalidInputs.hasInvalid ? INVALID_INPUTS_REASON : null;
  const postBlockReason = saveBlockReason ?? getCashOrderBlockReason(header);
  const canSave = saveBlockReason === null;

  /**
   * Ручное разнесение, которое уйдёт на сервер. Сетка существующего ордера засеяна всеми его
   * разнесениями, автоматическими тоже. Пока владелец сетку не правил, уходят только ручные
   * (`auto: false`): автоматические сервер пересчитает под новые сумму и документы. Иначе новая
   * сумма меньше прежнего разнесения (700 → 300) давала бы «Сумма разнесения превышает сумму
   * платежа», а любая правка (даже комментария) превращала бы автоматическое разнесение в ручное.
   */
  function manualAllocations() {
    if (rowsDirty) return toManualAllocations(rows); // правили сами — сервер проверит и откажет с причиной
    const derived = existingPayment ? existingPayment.allocations.filter((a) => !a.auto).map((a) => ({ documentId: a.documentId, amount: a.amount })) : toManualAllocations(rows);
    return clampAllocations(derived, header.amount);
  }

  function buildPayload(): SaveDocumentPayload {
    const keepFree = header.cashOperationType === 'PAYMENT' && noAutoAllocate;
    return {
      docDate: header.docDate,
      counterpartyId: header.counterpartyId,
      comment: header.comment || undefined,
      amount: header.amount ?? '0.00',
      method: header.method,
      cashOperationType: header.cashOperationType,
      noAutoAllocate: keepFree,
      // «Не разносить» прячет сетку: то, что в ней осталось (в том числе строка-основание, которую
      // подставило «Создать ордер» из документа), владелец уже не видит и разносить не хотел.
      allocations: keepFree ? [] : manualAllocations(),
    };
  }

  async function ensureCreated(): Promise<string> {
    if (documentId) return documentId;
    const created = await createDocument({ type, baseDocumentId, ...buildPayload() }).unwrap();
    setDocumentId(created.id);
    setDirty(false);
    return created.id;
  }

  /** Кнопки при неверном поле недоступны; сюда приходят и из диалога «Сохранить изменения?», и по
   *  горячей клавише — проверка повторяется здесь, а не только в `disabled` кнопки. */
  function refuseInvalidInput(): boolean {
    if (canSave) return false;
    message.warning(INVALID_INPUTS_HINT);
    return true;
  }

  /** То же для «Провести»/«Сохранить» проведённого: ордер должен быть полон. */
  function refuseIncompleteOrder(): boolean {
    if (refuseInvalidInput()) return true;
    if (postBlockReason === null) return false;
    message.warning(postBlockReason);
    return true;
  }

  async function handleSaveDraft(close: boolean) {
    if (busy || refuseInvalidInput()) return;
    try {
      if (!documentId) {
        await ensureCreated();
      } else {
        await updateDocument({ id: documentId, rowVersion: doc!.rowVersion, body: buildPayload() }).unwrap();
        setRowsDirty(false);
        setDirty(false);
      }
      message.success('Черновик сохранён');
      if (close) onClose();
    } catch (e) {
      handleMutationError(e);
    }
  }

  function warningTexts(warnings: LineWarningEntry[]): string[] {
    return describeWarnings(warnings, () => undefined);
  }

  function confirmWarnings(texts: string[]): Promise<boolean> {
    return new Promise((resolve) => {
      modal.confirm({
        title: 'Провести с предупреждениями?',
        content: (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {texts.map((text) => (
              <li key={text}>{text}</li>
            ))}
          </ul>
        ),
        okText: 'Провести',
        cancelText: 'Не проводить',
        okButtonProps: confirmButton(),
        cancelButtonProps: confirmButton(),
        width: 480,
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
  }

  async function handlePost(close: boolean) {
    if (busy || refuseIncompleteOrder()) return;
    setIsPosting_(true);
    try {
      const currentId = await ensureCreated();
      const rowVersion = documentId ? (doc?.rowVersion ?? 1) : 1;
      // Разнесение и «Не разносить» хранятся только на Payment, создаваемом при проведении, — в
      // черновике (в том числе только что созданном этим же нажатием) их хранить негде, поэтому
      // payload уходит всегда: иначе первое проведение нового ордера молча игнорирует и ручные
      // строки, и галочку, и деньги уходят в авто-FIFO.
      const payload = buildPayload();
      const preview = await previewDocument({ id: currentId, body: { action: 'post', rowVersion, document: payload } }).unwrap();
      const texts = warningTexts(preview.warnings);
      if (texts.length > 0 && !(await confirmWarnings(texts))) {
        message.info('Документ не проведён и остаётся черновиком');
        return;
      }
      const result = await postDocument({ id: currentId, body: { rowVersion, document: payload } }).unwrap();
      seedHeaderFrom(result.document);
      setRowsDirty(false);
      message.success(result.warnings.length > 0 ? 'Документ проведён с предупреждениями' : 'Документ проведён');
      if (close) onClose();
    } catch (e) {
      handleMutationError(e);
    } finally {
      setIsPosting_(false);
    }
  }

  async function handleSaveRepost(close: boolean) {
    if (busy || !documentId || !doc || refuseIncompleteOrder()) return;
    const payload = buildPayload();
    try {
      const saved = await runWithPreview(
        documentId,
        'post',
        doc.rowVersion,
        async (stateVersion) => {
          const result = await postDocument({ id: documentId, body: { rowVersion: doc.rowVersion, document: payload, stateVersion } }).unwrap();
          seedHeaderFrom(result.document);
        },
        payload,
      );
      if (saved) {
        setRowsDirty(false);
        message.success('Документ перепроведён');
        if (close) onClose();
      }
    } catch (e) {
      handleMutationError(e);
    }
  }

  /** Деструктивные действия (снять проведение, отменить, удалить) всегда переспрашивают, а фокус
   *  в диалоге стоит на «Отмена»: случайный Enter ничего не разрушает. */
  function handleUnpost() {
    if (busy || !documentId || !doc) return;
    modal.confirm({
      title: 'Снять проведение?',
      content: 'Остатки и расчёты по документу будут пересчитаны, а документ вернётся в черновик.',
      okText: 'Снять проведение',
      cancelText: 'Отмена',
      okButtonProps: confirmButton({ danger: true }),
      cancelButtonProps: confirmButton(),
      autoFocusButton: 'cancel',
      onOk: async () => {
        try {
          const saved = await runWithPreview(documentId, 'unpost', doc.rowVersion, (stateVersion) =>
            unpostDocument({ id: documentId, body: { rowVersion: doc.rowVersion, stateVersion } }).unwrap(),
          );
          if (saved) message.success('Проведение снято');
        } catch (e) {
          handleMutationError(e);
        }
      },
    });
  }

  function handleCancel() {
    if (busy || !documentId || !doc) return;
    modal.confirm({
      title: 'Отменить документ?',
      okText: 'Отменить документ',
      cancelText: 'Не отменять',
      okButtonProps: confirmButton({ danger: true }),
      cancelButtonProps: confirmButton(),
      autoFocusButton: 'cancel',
      onOk: async () => {
        try {
          const saved = await runWithPreview(documentId, 'cancel', doc.rowVersion, (stateVersion) =>
            cancelDocument({ id: documentId, body: { rowVersion: doc.rowVersion, stateVersion } }).unwrap(),
          );
          if (saved) message.success('Документ отменён');
        } catch (e) {
          handleMutationError(e);
        }
      },
    });
  }

  function handleDelete() {
    if (busy || !documentId || !doc) return;
    modal.confirm({
      title: 'Удалить документ?',
      okText: 'Удалить',
      cancelText: 'Отмена',
      okButtonProps: confirmButton({ danger: true }),
      cancelButtonProps: confirmButton(),
      autoFocusButton: 'cancel',
      onOk: async () => {
        try {
          const saved =
            status === 'POSTED'
              ? await runWithPreview(documentId, 'remove', doc.rowVersion, (stateVersion) =>
                  deleteDocument({ id: documentId, body: { rowVersion: doc.rowVersion, stateVersion } }).unwrap(),
                )
              : await deleteDocument({ id: documentId, body: { rowVersion: doc.rowVersion } })
                  .unwrap()
                  .then(() => true);
          if (saved) {
            message.success('Документ удалён');
            onClose();
          }
        } catch (e) {
          handleMutationError(e);
        }
      },
    });
  }

  async function handleReturnToDraft() {
    if (busy || !documentId || !doc) return;
    try {
      const result = await returnToDraft({ id: documentId, rowVersion: doc.rowVersion }).unwrap();
      seedHeaderFrom(result.document);
      message.success('Документ возвращён в черновик');
    } catch (e) {
      handleMutationError(e);
    }
  }

  async function handleRestore() {
    if (busy || !documentId || !doc) return;
    try {
      const result = await restoreDocument({ id: documentId, rowVersion: doc.rowVersion }).unwrap();
      seedHeaderFrom(result.document);
      message.success(result.document.status === 'DRAFT' ? 'Документ восстановлен как черновик — проведите его заново' : 'Документ восстановлен');
    } catch (e) {
      handleMutationError(e);
    }
  }

  // ── Закрытие с вопросом и горячие клавиши ────────────────────────────────────────────────
  const { requestClose, dialog: unsavedDialog } = useUnsavedClose({
    dirty,
    onClose,
    onSave: () => void (status === 'DRAFT' ? handleSaveDraft(true) : handleSaveRepost(true)),
    saveLabel: status === 'DRAFT' ? 'Сохранить черновик' : 'Сохранить',
    saving: busy,
  });

  // Клавиша приходит раньше, чем поле «Сумма» отдало окну набранное (фиксируется на blur), а
  // замыкания биндингов — от прошлой перерисовки: сначала фиксируем активное поле, затем берём
  // обработчик из `latest` (свежий после перерисовки). Каждый обработчик ещё раз проверяет `busy`
  // и полноту. Подробно — в DocumentWindow.
  const latest = useLatest({ handlePost, handleSaveDraft, handleSaveRepost, handleUnpost, handleCancel, handleDelete, handleReturnToDraft, handleRestore });
  const committed = (pick: (h: typeof latest.current) => unknown) => () => {
    if (commitActiveField().invalid) {
      message.warning(INVALID_INPUTS_HINT);
      return;
    }
    void pick(latest.current);
  };
  const notSaved = documentId ? false : 'Документ ещё не сохранён';
  const actions = useHotkeyActions(
    isDeleted
      ? { 'doc.primary': { label: 'Восстановить', hint: false, run: committed((h) => h.handleRestore()) } }
      : status === 'DRAFT'
        ? {
            'doc.primary': { alwaysRun: true, disabled: postBlockReason ?? false, run: committed((h) => h.handlePost(true)) },
            'doc.post': { alwaysRun: true, disabled: postBlockReason ?? false, run: committed((h) => h.handlePost(false)) },
            'doc.save': { label: 'Сохранить черновик', alwaysRun: true, disabled: saveBlockReason ?? false, run: committed((h) => h.handleSaveDraft(false)) },
            'doc.delete': { hint: false, disabled: notSaved, run: committed((h) => h.handleDelete()) },
          }
        : status === 'POSTED'
          ? {
              'doc.primary': { label: 'Сохранить и закрыть', alwaysRun: true, disabled: postBlockReason ?? false, run: committed((h) => h.handleSaveRepost(true)) },
              'doc.save': { label: 'Сохранить', alwaysRun: true, disabled: postBlockReason ?? false, run: committed((h) => h.handleSaveRepost(false)) },
              'doc.unpost': { hint: false, run: committed((h) => h.handleUnpost()) },
              'doc.cancel': { hint: false, run: committed((h) => h.handleCancel()) },
              'doc.delete': { hint: false, run: committed((h) => h.handleDelete()) },
            }
          : { 'doc.primary': { label: 'Вернуть в черновик', hint: false, run: committed((h) => h.handleReturnToDraft()) } },
  );

  return (
    <>
      <PreviewConsequencesDialog diff={pendingDiff} warningTexts={pendingDiff ? warningTexts(pendingDiff.warnings) : []} onResolve={resolvePending} />
      {unsavedDialog}
      <FloatingWindow
        kind={`document:${type}`}
        title={windowTitle(type, doc, isNew)}
        onClose={requestClose}
        footer={isLoadingWindow ? null : renderFooter()}
        hotkeys={isLoadingWindow ? undefined : actions.bindings}
        // На телефоне курсор при открытии не ставим: он сразу поднял бы экранную клавиатуру.
        initialFocus={isNew && !isMobile ? 'auto' : 'window'}
        loading={isLoadingWindow}
      >
        <InvalidInputsContext.Provider value={invalidInputs.api}>
          <Flex vertical gap={isMobile ? 12 : 8}>
            {isNew ? (
              <Segmented
                block={isMobile}
                value={header.cashOperationType}
                onChange={(v) => editHeader({ cashOperationType: v as CashOrderHeader['cashOperationType'] })}
                options={[
                  { value: 'PAYMENT', label: 'Оплата' },
                  { value: 'REFUND', label: 'Возврат денег' },
                ]}
              />
            ) : (
              <Typography.Text style={{ fontSize: labelSize }} type="secondary">
                Операция: {header.cashOperationType === 'PAYMENT' ? 'Оплата' : 'Возврат денег'}
              </Typography.Text>
            )}

            <Flex gap={8} wrap vertical={isMobile}>
              <Flex vertical style={isMobile ? MOBILE_FIELD : { minWidth: 220, flex: 1 }}>
                <Typography.Text style={{ fontSize: labelSize }}>Контрагент</Typography.Text>
                <CounterpartySelect
                  value={header.counterpartyId}
                  current={counterpartyRef}
                  disabled={isReadOnly}
                  onChange={(v) => editHeader({ counterpartyId: v })}
                />
              </Flex>
              <Flex vertical style={isMobile ? MOBILE_FIELD : { minWidth: 140 }}>
                <Typography.Text style={{ fontSize: labelSize }}>Дата</Typography.Text>
                <DatePicker
                  value={dayjs(header.docDate)}
                  format="DD.MM.YYYY"
                  inputReadOnly={isMobile}
                  disabled={isReadOnly}
                  onChange={(d) => editHeader(d ? { docDate: d.format('YYYY-MM-DD') } : {})}
                />
              </Flex>
            </Flex>

            <Flex gap={8} wrap vertical={isMobile}>
              {/* Новый ордер открывается на «Сумма»: контрагент и вид чаще всего уже подставлены основанием. */}
              <Flex
                vertical
                className={isMobile ? 'cash-amount-field' : undefined}
                style={isMobile ? MOBILE_FIELD : { minWidth: 140 }}
                data-autofocus={isMobile ? undefined : true}
              >
                <Typography.Text style={{ fontSize: labelSize }}>Сумма</Typography.Text>
                <MoneyInput value={header.amount} disabled={isReadOnly} onChange={(v) => editHeader({ amount: v })} />
              </Flex>
              <Flex vertical style={isMobile ? MOBILE_FIELD : { minWidth: 160 }}>
                <Typography.Text style={{ fontSize: labelSize }}>Способ</Typography.Text>
                {isMobile ? (
                  // Три способа — переключателем на всю ширину: один тап вместо открытия списка.
                  <Segmented<PaymentMethod> block value={header.method} disabled={isReadOnly} options={METHOD_OPTIONS} onChange={(v) => editHeader({ method: v })} />
                ) : (
                  <Select<PaymentMethod>
                    value={header.method}
                    disabled={isReadOnly}
                    options={METHOD_OPTIONS}
                    style={{ width: '100%' }}
                    onChange={(v) => editHeader({ method: v })}
                  />
                )}
              </Flex>
              <Flex vertical style={isMobile ? MOBILE_FIELD : { minWidth: 200, flex: 1 }}>
                <Typography.Text style={{ fontSize: labelSize }}>Комментарий</Typography.Text>
                <Input value={header.comment} disabled={isReadOnly} onChange={(e) => editHeader({ comment: e.target.value })} />
              </Flex>
            </Flex>

            {header.cashOperationType === 'PAYMENT' ? (
              <Checkbox
                style={isMobile ? { minHeight: 44, alignItems: 'center' } : undefined}
                checked={noAutoAllocate}
                disabled={isReadOnly}
                onChange={(e) => {
                  setDirty(true);
                  setNoAutoAllocate(e.target.checked);
                }}
              >
                Не разносить (оставить свободным авансом)
              </Checkbox>
            ) : null}

            {/* Итоги разнесения — липкой полосой: сумма ордера, разнесено, сколько осталось. */}
            {isMobile && header.counterpartyId && !(header.cashOperationType === 'PAYMENT' && noAutoAllocate) ? renderAllocationTotals() : null}

            {header.counterpartyId && !(header.cashOperationType === 'PAYMENT' && noAutoAllocate) ? (
              <AllocationGrid
                title={target.title}
                remainderTitle={target.remainderTitle}
                emptyText={header.cashOperationType === 'REFUND' ? 'Нет незакрытых возвратов' : undefined}
                rows={rows}
                onChange={editRows}
                loading={isFetchingSideList || isLoadingPayment}
              />
            ) : null}

            {invalidInputs.hasInvalid ? (
              <Typography.Text type="danger" style={{ fontSize: labelSize }}>
                {INVALID_INPUTS_HINT}
              </Typography.Text>
            ) : null}

            <AttachmentsBlock documentId={documentId} readOnly={isReadOnly} />
          </Flex>
        </InvalidInputsContext.Provider>
      </FloatingWindow>
    </>
  );

  /** Телефон: «Сумма / Разнесено / Остаток» по ордеру. Пустая ручная сетка у оплаты — не «остаток», а
   *  автоматическое разнесение (FIFO) на сервере: об этом сказано в примечании. */
  function renderAllocationTotals() {
    const amount = header.amount ? dec(header.amount).toFixed(2) : '0.00';
    const allocated = sumAmounts(rows);
    const rest = dec(amount).minus(dec(allocated));
    const auto = header.cashOperationType === 'PAYMENT' && !noAutoAllocate && dec(allocated).isZero();
    return (
      <AllocationTotals
        items={[
          { label: 'Сумма', value: amount },
          { label: 'Разнесено', value: allocated },
          { label: 'Остаток', value: rest.toFixed(2), tone: rest.isNegative() ? 'warn' : undefined },
        ]}
        note={auto ? 'Если не указать вручную, сумма разнесётся автоматически (FIFO)' : undefined}
      />
    );
  }

  /** Телефон: главное действие статуса — на всю ширину, остальное — в меню «⋯»; причина, по которой
   *  главное недоступно, видна текстом (подсказки по наведению на телефоне нет). */
  function renderMobileFooter() {
    const closeItem = { key: 'close', label: 'Закрыть', onClick: requestClose, style: MOBILE_MENU_ITEM };
    const item = (key: string, label: string, id: 'doc.post' | 'doc.save' | 'doc.unpost' | 'doc.cancel' | 'doc.delete', danger = false) => {
      const action = actions.get(id);
      return { key, label, danger, disabled: action.disabled || busy, onClick: () => action.run(), style: MOBILE_MENU_ITEM };
    };
    const primary = actions.get('doc.primary');

    let primaryLabel: string;
    let items: MenuProps['items'];
    if (isDeleted) {
      primaryLabel = 'Восстановить';
      items = [closeItem];
    } else if (status === 'DRAFT') {
      primaryLabel = 'Провести и закрыть';
      items = [item('post', 'Провести', 'doc.post'), item('save', 'Сохранить черновик', 'doc.save'), item('delete', 'Удалить', 'doc.delete', true), closeItem];
    } else if (status === 'POSTED') {
      primaryLabel = 'Сохранить и закрыть';
      items = [
        item('save', 'Сохранить', 'doc.save'),
        item('unpost', 'Снять проведение', 'doc.unpost'),
        item('cancel', 'Отменить', 'doc.cancel'),
        item('delete', 'Удалить', 'doc.delete', true),
        closeItem,
      ];
    } else {
      primaryLabel = 'Вернуть в черновик';
      items = [closeItem];
    }

    return (
      <Flex vertical gap={6}>
        {primary.disabledReason ? (
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            {primary.disabledReason}
          </Typography.Text>
        ) : null}
        <Flex gap={8}>
          <Button type="primary" size="large" loading={busy} disabled={primary.disabled} onClick={primary.run} style={{ flex: 1, minWidth: 0 }}>
            {primaryLabel}
          </Button>
          <Dropdown menu={{ items }} trigger={['click']} placement="topRight">
            <Button size="large" icon={<EllipsisOutlined />} aria-label="Ещё" style={{ width: 48, flex: '0 0 auto' }} />
          </Dropdown>
        </Flex>
      </Flex>
    );
  }

  function renderFooter() {
    if (isFetching && documentId) return <Typography.Text type="secondary">Загрузка…</Typography.Text>;
    if (isMobile) return renderMobileFooter();

    const closeButton = (
      <HotkeyTip catalogId="window.close">
        <Button onClick={requestClose}>Закрыть</Button>
      </HotkeyTip>
    );

    if (isDeleted) {
      return (
        <Space wrap>
          <HotkeyButton type="primary" loading={busy} action={actions.get('doc.primary')}>
            Восстановить
          </HotkeyButton>
          {closeButton}
        </Space>
      );
    }
    if (status === 'DRAFT') {
      return (
        <Space wrap>
          <HotkeyButton type="primary" loading={busy} action={actions.get('doc.primary')}>
            Провести и закрыть
          </HotkeyButton>
          <HotkeyButton loading={busy} action={actions.get('doc.post')}>
            Провести
          </HotkeyButton>
          <HotkeyButton loading={busy} action={actions.get('doc.save')}>
            Сохранить черновик
          </HotkeyButton>
          <HotkeyButton danger loading={busy} action={actions.get('doc.delete')}>
            Удалить
          </HotkeyButton>
          {closeButton}
        </Space>
      );
    }
    if (status === 'POSTED') {
      return (
        <Space wrap>
          <HotkeyButton type="primary" loading={busy} action={actions.get('doc.primary')}>
            Сохранить и закрыть
          </HotkeyButton>
          <HotkeyButton loading={busy} action={actions.get('doc.save')}>
            Сохранить
          </HotkeyButton>
          <HotkeyButton loading={busy} action={actions.get('doc.unpost')}>
            Снять проведение
          </HotkeyButton>
          <HotkeyButton loading={busy} action={actions.get('doc.cancel')}>
            Отменить
          </HotkeyButton>
          <HotkeyButton danger loading={busy} action={actions.get('doc.delete')}>
            Удалить
          </HotkeyButton>
          {closeButton}
        </Space>
      );
    }
    // CANCELLED
    return (
      <Space wrap>
        <HotkeyButton loading={busy} action={actions.get('doc.primary')}>
          Вернуть в черновик
        </HotkeyButton>
        {closeButton}
      </Space>
    );
  }
}
